import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, Ref } from 'react';

import { L10nDate } from '@travel/i18n';
import { IconProps } from '@travel/icons';
import SimpleDateButton from '@travel/traveler-core/components/DateInput/SimpleDateButton';
import SmallDateButton from '@travel/traveler-core/components/DateInput/SmallDateButton';
import {
  useDeviceType,
  useTranslation,
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
} from '@travel/traveler-core/hooks';
import { seoKeys } from '@travel/traveler-core/utils/seo';
import { DateButtonGroup, FlatButton } from '@travel/ui';
import { DisplayType } from '@travel/ui/components/DatePicker';
import { cx } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import LocaleDatePicker from 'components/LocaleDatePicker';
import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import { Translate } from 'core/translate';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import styles from './dateInput.module.scss';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const DATE_FORM_FORMAT = 'YYYYMMDD';
type DateComponentType = { value: string; format?: string };
type SelectedRange = { start?: string; end?: string };

export type DateRef = {
  resetDate: () => void;
} | null;

export type Props = {
  /** Custom style for wrapper */
  className?: string;
  /** String of date format to be displayed */
  displayFormat?: string;
  /** End date of selected range */
  endDate?: string;
  /** Date to define the first calendar */
  refDate?: string;
  /** Start date of selected range */
  startDate?: string;
  /** on what parent page this component lives */
  onPage:
    | 'top'
    | 'providerListDated'
    | 'providerListUndated'
    | 'providerInfoDated'
    | 'providerInfoUndated'
    | 'couponSearch'
    | 'area'
    | 'rateplan'
    | 'modification';
  /** The callback function when ok button is clicked (on SP/TL) */
  onClickOkButton?: () => void;
  /** The callback function when the dialog was closed and state has been changed */
  onDateChanged?: (startDate?: string, endDate?: string) => void;
  /** Date to define the end of select day */
  refEndDate?: string;
  /** Whether date picker auto open after selected place */
  isAutoOpenDatePicker?: boolean;
  /** Set auto open date picker */
  setIsAutoOpenDatePicker?: (autoOpen: boolean) => void;
  /** inner Ref */
  innerRef?: Ref<DateRef>;
};

const PROVIDER_LIST_ICON_PROPS: IconProps = { color: 'cilantro' };

function DateInput(props: Props) {
  const { displayFormat = 'M/D/YYYY' } = props;
  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;
  const isPC = deviceType === DESKTOP_SCREEN;

  const referenceDate = dayjs(props.refDate).format(DATE_FORM_FORMAT);
  const referenceEndDate = props.refEndDate && dayjs(props.refEndDate).format(DATE_FORM_FORMAT);
  const DateComponent = (props: DateComponentType) => <L10nDate format="L" {...props} />;
  const DateComponentSmall = (props: DateComponentType) => <L10nDate format="MD" {...props} />;
  const NightsLabelComponent = (props: { noOfNights: number }) => (
    <Translate
      id="Traveler_Common.Calendar.Term.Nights"
      count={props.noOfNights}
      data={{ number_of_nights: props.noOfNights }}
    />
  );

  let mainElementRef = useRef<HTMLDivElement>(null);
  const [popupMinWidth, setPopupMinWidth] = useState<number | undefined>(undefined);

  // selected date range handler
  const [selectedRanged, setSelectedRange] = useState<SelectedRange>({
    start: props.startDate && dayjs(props.startDate).format(DATE_FORM_FORMAT),
    end: props.endDate && dayjs(props.endDate).format(DATE_FORM_FORMAT),
  });

  // button active state handler
  const isInvalidDate = !selectedRanged.start && !selectedRanged.end;

  // open dialog state handler
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedButton, setSelectedButton] = useState('startDate');
  const isSelectedCheckInButton = selectedButton === 'startDate';

  const { pageType, siteSection, parentName } = RAT_PAGE_TYPE_OBJ[props.onPage];

  const startDateLabel = useTranslation({ id: 'Top.Dates.Check_in' });
  const endDateLabel = useTranslation({ id: 'Top.Dates.Check_out' });

  // page
  const isProviderListPage =
    props.onPage === 'providerListUndated' || props.onPage === 'providerListDated';
  const isProviderInfoPage =
    props.onPage === 'providerInfoUndated' || props.onPage === 'providerInfoDated';
  const isCouponSearchPage = props.onPage === 'couponSearch';
  const isRatePlanItem = props.onPage === 'rateplan';
  const isModification = props.onPage === 'modification';

  // to verify the changes of dates
  let prevSelectedRanged = useRef<SelectedRange | null>(null);

  useImperativeHandle(props.innerRef, () => ({
    resetDate: () => {
      setSelectedButton('startDate');
      setSelectedRange({
        start: '',
        end: '',
      });
    },
  }));

  const verifyDateChanged = useCallback(() => {
    const { start, end } = selectedRanged;
    const { start: prevStart, end: prevEnd } = prevSelectedRanged.current || {};
    // reset value
    prevSelectedRanged.current = null;
    if (start !== prevStart || end !== prevEnd) {
      props.onDateChanged?.(start, end);
    }
  }, [props.onDateChanged, selectedRanged]);

  const onClickCloseDialog = useCallback(() => {
    props.onDateChanged && verifyDateChanged();
    setIsDatePickerOpen(false);
    props.setIsAutoOpenDatePicker?.(false);
  }, [props, verifyDateChanged]);

  const onClickStartDateButton = useCallback(() => {
    setSelectedButton('startDate');
    setIsDatePickerOpen(true);
  }, []);

  const onClickEndDateButton = useCallback(() => {
    setSelectedButton('endDate');
    setIsDatePickerOpen(true);
  }, []);

  const onSelectDateButton = useCallback((_event: React.MouseEvent, button: string) => {
    setSelectedButton(button);
  }, []);

  useEffect(() => {
    if (props.isAutoOpenDatePicker) {
      setIsDatePickerOpen(true);
    }
  }, [props.isAutoOpenDatePicker]);

  const onSelectDate = useCallback(
    (_event: React.MouseEvent, day: Dayjs) => {
      // set selected range state
      setSelectedRange(prevState => {
        props.setIsAutoOpenDatePicker?.(false);
        const { start, end } = prevState;
        const formattedDate = day.format(DATE_FORM_FORMAT);
        if (isSelectedCheckInButton) {
          // if the check-in button is activate
          // ; when the given start date is same or after the selected end date,
          // then reset the end date and activate the check-out button
          if (end && day.isSameOrAfter(dayjs(end))) {
            setSelectedButton('endDate');
            return { start: formattedDate, end: undefined };
          }
          // ; when the selected end date is not define, then activate the check-out button
          if (!end) {
            setSelectedButton('endDate');
          }
          // ; when the given start date is before the selected end date, then use it as selected start date
          return { start: formattedDate, end };
        } else {
          // if the check-out button is activate
          // ; when the given end date is same or before the selected start date, then reset the end date
          // ; and use it as selected start date
          if (start && day.isSameOrBefore(dayjs(start))) {
            return { start: formattedDate, end: undefined };
          }
          // ; when the selected start date is not define, then activate the check-in button
          if (!start) {
            setSelectedButton('startDate');
          }
          // ; when the given end date is after the selected start date, then use it as selected end date
          return { start, end: formattedDate };
        }
      });
    },
    [isSelectedCheckInButton, props.setIsAutoOpenDatePicker],
  );

  // to update state when prop has been changed
  useEffect(() => {
    setSelectedRange({
      start: props.startDate && dayjs(props.startDate).format(DATE_FORM_FORMAT),
      end: props.endDate && dayjs(props.endDate).format(DATE_FORM_FORMAT),
    });
  }, [props.startDate, props.endDate]);

  // to store the prev state to verify if there is any changes
  useEffect(() => {
    if (props.onDateChanged && !prevSelectedRanged.current && isDatePickerOpen) {
      prevSelectedRanged.current = selectedRanged;
    }
  }, [isDatePickerOpen, props.onDateChanged, selectedRanged]);

  // calculate the main element position to show the date-picker dialog (PC)
  useEffect(() => {
    if (isPC && isDatePickerOpen && mainElementRef.current) {
      const mainElementRect = mainElementRef.current.getBoundingClientRect();
      setPopupMinWidth(mainElementRect.width);
    }
  }, [isPC, isDatePickerOpen]);

  // To close the dialog when both dates are set
  useEffect(() => {
    let closeDialogTimer: NodeJS.Timeout;
    if (selectedRanged.start && selectedRanged.end && isPC && !props.isAutoOpenDatePicker) {
      // XXX: This delay is to give user a glance of his selected date
      closeDialogTimer = setTimeout(() => onClickCloseDialog(), 600);
    }

    return () => {
      clearTimeout(closeDialogTimer);
    };
  }, [selectedRanged, onClickCloseDialog, isPC, props.isAutoOpenDatePicker]);

  // default props
  const defaultSelectedDateProps = {
    DateComponent: DateComponent,
    startDateLabel,
    endDateLabel,
    startDate: selectedRanged.start && dayjs(selectedRanged.start).format(displayFormat),
    endDate: selectedRanged.end && dayjs(selectedRanged.end).format(displayFormat),
  };

  const defaultDateButtonGroupProps = {
    selectedButton,
    onClickStartDateButton,
    onClickEndDateButton,
    isShowSelectedState: isDatePickerOpen,
  };

  // custom DatePicker props based on device
  let customDatePickerProps;

  const onClearDatePicker = useCallback(() => {
    setSelectedRange({ start: '', end: '' });
    setSelectedButton('startDate');
  }, []);

  // will be used for mobile/tablet date-picker only
  const onClickPrimary = useCallback(() => {
    onClickCloseDialog();
    props.onClickOkButton?.();
  }, [onClickCloseDialog, props.onClickOkButton]);

  if (!isPC) {
    const nights =
      selectedRanged.start && selectedRanged.end
        ? dayjs(selectedRanged.end).diff(selectedRanged.start, 'day')
        : 0;
    customDatePickerProps = {
      // 2 Years included current month
      numberOfMonths: 25,
      type: 'dialog' as DisplayType,
      dialogProps: {
        buttonLabelPrimary: (
          <>
            {isProviderInfoPage ? (
              <Translate id="Provider_Info.Update" />
            ) : (
              <Translate id="Traveler_Common.OK" />
            )}
            {selectedRanged.start && selectedRanged.end && (
              <>
                &nbsp;
                <Translate
                  id="Traveler_Common.Calendar.Term.Nights"
                  data={{
                    number_of_nights: nights,
                  }}
                  count={nights}
                />
              </>
            )}
          </>
        ),
        onClickPrimary,
      },
    };
  } else {
    customDatePickerProps = {
      numberOfMonths: 2,
      type: 'popup' as DisplayType,
      popupProps: { anchorEl: mainElementRef.current, minWidth: popupMinWidth },
    };
  }

  // form value
  let formCheckInDate = selectedRanged.start;
  let formCheckOutDate = selectedRanged.end;

  if (formCheckInDate && !formCheckOutDate) {
    formCheckOutDate = dayjs(formCheckInDate)
      .add(1, 'day')
      .format(DATE_FORM_FORMAT);
  }

  if (!formCheckInDate && formCheckOutDate) {
    formCheckInDate = dayjs(referenceDate)
      .add(1, 'day')
      .format(DATE_FORM_FORMAT);
  }

  // to handle the case that user select checkout date as today
  if (dayjs(formCheckInDate).isAfter(dayjs(formCheckOutDate))) {
    formCheckInDate = dayjs(formCheckOutDate).format(DATE_FORM_FORMAT);
    formCheckOutDate = dayjs(formCheckInDate)
      .add(1, 'day')
      .format(DATE_FORM_FORMAT);
  }

  return (
    <div className={props.className} ref={mainElementRef} data-testid="dateInput-wrapper">
      {formCheckInDate && (
        <input
          type="hidden"
          data-testid="dateInput-startDate-hiddenInput"
          name={seoKeys.startDate}
          value={formCheckInDate}
        />
      )}
      {formCheckOutDate && (
        <input
          type="hidden"
          data-testid="dateInput-endDate-hiddenInput"
          name={seoKeys.endDate}
          value={formCheckOutDate}
        />
      )}

      {isSP && (props.onPage === 'top' || props.onPage === 'area') && (
        <SmallDateButton
          className={styles.topAreaSmallButton}
          startDate={formCheckInDate}
          endDate={formCheckOutDate}
          onClick={onClickStartDateButton}
          DateComponent={DateComponentSmall}
        />
      )}

      {!isSP && (props.onPage === 'top' || props.onPage === 'area') && (
        <DateButtonGroup
          className={cx(styles.topAreaButtonGroup, isDatePickerOpen && styles.activeBorder)}
          buttonClassName={styles.topPageButton}
          unhoveredColor="default"
          hoveredColor="default"
          {...defaultDateButtonGroupProps}
          {...defaultSelectedDateProps}
        />
      )}
      {isProviderListPage && !isPC && (
        <SimpleDateButton
          className={styles.providerListButton}
          startDate={formCheckInDate}
          endDate={formCheckOutDate}
          onClick={onClickStartDateButton}
          DateComponent={DateComponent}
        />
      )}

      {((isProviderListPage && isPC) || (isProviderInfoPage && !isSP)) && (
        <DateButtonGroup
          className={cx(
            styles.providerListButtonGroup,
            isDatePickerOpen && styles.activeBorder,
            isProviderInfoPage && styles.providerInfoButtonGroup,
          )}
          buttonClassName={cx(styles.providerListButton, isInvalidDate && styles.placeholderColor)}
          buttonIconProps={PROVIDER_LIST_ICON_PROPS}
          unhoveredColor="default"
          hoveredColor="default"
          {...defaultDateButtonGroupProps}
          {...defaultSelectedDateProps}
        />
      )}

      {isProviderInfoPage && isSP && (
        <SimpleDateButton
          startDate={formCheckInDate}
          endDate={formCheckOutDate}
          customDatelessText={<Translate id="Provider_Info.Filter.Date" />}
          onClick={onClickStartDateButton}
          DateComponent={DateComponent}
        />
      )}
      {isCouponSearchPage && (
        <>
          {isSP ? (
            <SimpleDateButton
              className={cx(styles.couponSearchSimpleButton, styles.couponSearchButtonIcon)}
              iconClassName={styles.couponButtonIcon}
              startDate={formCheckInDate}
              endDate={formCheckOutDate}
              customDatelessText={<Translate id="Top.Search.Date_Selection" />}
              iconSize={16}
              onClick={onClickStartDateButton}
              DateComponent={DateComponent}
            />
          ) : (
            <DateButtonGroup
              className={cx(styles.topPageButtonGroup, styles.couponSearchButtonIcon)}
              buttonClassName={styles.topPageButton}
              {...defaultDateButtonGroupProps}
              {...defaultSelectedDateProps}
            />
          )}
        </>
      )}
      {isRatePlanItem && (
        <FlatButton
          data-testid="dateInput-chooseDate-button"
          ariaLabel="Choose date"
          onClick={onClickStartDateButton}
          isSmallerButtonByDefault={true}
        >
          <Translate id="Provider_Info.Room_List.Plan_Info.Choose_Date" />
        </FlatButton>
      )}

      {isModification && (
        <DateButtonGroup
          className={styles.modificationButtonGroup}
          buttonClassName={styles.modificationPageButton}
          {...defaultDateButtonGroupProps}
          {...defaultSelectedDateProps}
        />
      )}

      <LocaleDatePicker
        className={styles[`picker--${props.onPage}`]}
        selectedButton={selectedButton}
        isOpen={isDatePickerOpen}
        onSelectDate={onSelectDate}
        referenceDate={referenceDate}
        referenceEndDate={referenceEndDate}
        onSelectButton={onSelectDateButton}
        onClose={onClickCloseDialog}
        onClear={onClearDatePicker}
        isDisplayedOnDialog={isCouponSearchPage}
        NightsLabelComponent={NightsLabelComponent}
        shouldShowCurrentDate={true}
        {...defaultSelectedDateProps}
        {...customDatePickerProps}
      />
      {isDatePickerOpen && !isPC && (
        <DataLayer
          pageName={`${parentName}:dates_calendar_popup`}
          pageType={pageType}
          siteSection={siteSection}
        />
      )}
    </div>
  );
}

export default DateInput;
